import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  Suspense,
  useRef,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getDecryptedValue,
  generateHashedKey,
  safeJsonParse,
  useExtractClaimTotals,
  setEncryptedValue,
  DeviceTypes,
  useDeviceType,
  buildClaimsIndex,
  getClaimById,
  showPayButton,
  ErrorModal,
  useEobDownload,
  domainForSegment,
  NavigateConst,
  useAnalytics,
} from 'common';
import { getClaimDateRange } from 'common/utils/ClaimUtils';
import { GlobalContext, isAvMed } from 'context';
import {
  useMedicalClaimSearch,
  ClaimResponse,
  useConsolidateSUmmary,
} from '@sentara-health/sentara-api-hooks-core';
import { Headings, Loader, Link, Paragraph } from '@sentara-health/optimahealth_web';
import ApiWrapper from 'common/ApiWrapper';
import {
  SuperEOBHeader,
  SuperEOBSummaryCards,
  SuperEOBBreakdown,
} from './SuperEOB';
import AnnualBenefitSummary from './SuperEOB/AnnualBenefitSummary';
import PaymentSection from './SuperEOB/PaymentSection';

const SuperEOBClaims: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { claimId, mId, memberId, memberName, showMedical } = location.state ?? {};
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const { medicalClaimSearch } = useMedicalClaimSearch();
  const { getFeature, serviceNumber } = useContext(GlobalContext);
  const [claimData, setClaimData] = useState<ClaimResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState<{
    data?: { benefitPhone1800?: string;};
  }>({});
  const { downloadEobPdf, isDownloading, hasError, clearError } = useEobDownload(showMedical, mId);
  const { trackContentListing } = useAnalytics();

  const trackClaimsAdobeEvent = (
    contentId: string,
    contentTitle: string,
    itemPosition: number,
    additionalFields: Record<string, string | number> = {}
  ) => {
    trackContentListing(
      contentId,
      contentTitle,
      itemPosition,
      undefined,
      { extraFields: additionalFields }
    );
  };

  const trackClaimsLoadError = (_errorCode?: string | number) => {
    const isBehavioralClaim = showMedical === false;
    const contentTitle = isBehavioralClaim ? 'Behavioral_claim_EOB_load_error_view' : 'Medical_claim_EOB_load_error_view';
    trackContentListing(
      isBehavioralClaim ? '1227' : '1223',
      contentTitle,
      isBehavioralClaim ? 19 : 15,
      `${domainForSegment}${isBehavioralClaim ? '/home/behavioral-claims' : '/home/medical-claims'}`
    );
  };

  const handleDownloadEob = (id: string) => {
    downloadEobPdf(id);
  };

  const handlePayBill = () => {
    trackClaimsAdobeEvent('1212', `${showMedical === false ? 'Behavioral' : 'Medical'}_Claims_pay_your_bill_click`, 6);
    navigate(NavigateConst.paySentaraBillURL);
  };

  const emptyClaimResponse: ClaimResponse = {
    total: [],
    payment: undefined,
  } as ClaimResponse;

  const totals = useExtractClaimTotals(claimData || emptyClaimResponse);
  const { ConsolidateSUmmary } = useConsolidateSUmmary();
  const {
    data: consolidatedData,
    callApi: getConsolidated,
  }: any = ApiWrapper(ConsolidateSUmmary);

  const hasFetchedClaims = useRef(false);

  const benefitSummary = React.useMemo(() => {
    if (!Array.isArray(consolidatedData) || consolidatedData.length === 0)
      return null;

    const deductibleData = consolidatedData.find((item: any) =>
      item.benefitDescription?.toUpperCase().includes('DEDUCTIBLE')
    );

    const moopData = consolidatedData.find(
      (item: any) =>
        item.benefitDescription?.toUpperCase().includes('MOOP') ||
        item.benefitDescription?.toUpperCase().includes('OUT OF POCKET')
    );

    if (!deductibleData || !moopData) return null;

    return {
      deductible: {
        name: deductibleData.member.name,
        amount: deductibleData.member.amount,
        limit: deductibleData.member.limit,
      },
      moop: {
        name: moopData.member.name,
        amount: moopData.member.amount,
        limit: moopData.member.limit,
      },
    };
  }, [consolidatedData]);

  const fetchClaimData = useCallback(async () => {
    if (!mId || !claimId || !memberId) return;

    setLoading(true);

    try {
      const cacheKey = await generateHashedKey(
        `${showMedical ? 'Medical' : 'Behavioral'}_Claims`,
        memberId
      );
      const cachedValue = await getDecryptedValue(cacheKey);

      if (cachedValue) {
        const parsed = safeJsonParse<ClaimResponse[]>(cachedValue, []);
        const claimsIndex = buildClaimsIndex(parsed);
        const cachedClaim = getClaimById(claimsIndex, claimId);
        if (cachedClaim) {
          setClaimData(cachedClaim);
          return;
        }
      }

      const { startDate, endDate } = getClaimDateRange();
      const body = {
        identifier: [
          { system: 'MemberId', value: memberId },
          { system: 'ClaimId', value: claimId },
        ],
        claimType: showMedical ? 0 : 1,
        startDate,
        endDate,
      };

      const response = await medicalClaimSearch(body);
      if (response?.error || response?.errorCode) {
        trackClaimsLoadError(response?.errorCode);
        return;
      }
      const apiClaim: ClaimResponse | null = Array.isArray(response?.data)
        ? response.data.find((c: any) =>
            c?.identifier?.some(
              (i: any) => i?.system?.includes('claimId') && i?.value === claimId
            )
          ) ?? response.data[0]
        : response?.data ?? null;

      if (apiClaim) {
        try {
          setEncryptedValue(cacheKey, JSON.stringify([apiClaim]));
        } catch {}
        setClaimData(apiClaim);
      }
    } catch (err) {
      trackClaimsLoadError();
      console.error('Error fetching claim data:', err);
    } finally {
      setLoading(false);
    }
  }, [mId, memberId, claimId, medicalClaimSearch, showMedical]);

  useEffect(() => {
    if (hasFetchedClaims.current) return;
    if (!mId || !claimId) return;

    hasFetchedClaims.current = true;
    fetchClaimData();

    setPhone(
      typeof serviceNumber === 'string'
        ? safeJsonParse(serviceNumber, {})
        : { data: serviceNumber }
    );
  }, [memberId, claimId, fetchClaimData, serviceNumber]);

  useEffect(() => {
    getConsolidated('1');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderContent = () => {
    if (loading) {
      return null;
    }

    if (claimData) {
      return (
        <>
          <div className="print-header">
            <SuperEOBHeader
              claimData={claimData}
              claimId={claimId}
              memberName={memberName}
              memberId={memberId}
              showMedical={showMedical}
              onDownloadEOB={getFeature?.Eob ? handleDownloadEob : undefined}
              isDownloading={isDownloading}
              phoneNumber={phone?.data?.benefitPhone1800}
            />
          </div>

          <SuperEOBSummaryCards
            totalCharges={totals.totalCharges}
            totalPaidByPlan={totals.totalPaidByPlan}
            patientResponsibility={totals.patientResponsibility}
          />
          {isMobile ? <hr className="my-4" /> : null}

          <SuperEOBBreakdown claimData={claimData} totals={totals} />
          {benefitSummary ? (
            <Suspense fallback={<Loader />}>
              <div className="annual-benefit-summary mt-5">
                <AnnualBenefitSummary benefitData={benefitSummary} />
                <hr className="d-md-none d-block" />
              </div>
            </Suspense>
          ) : null}
          <PaymentSection 
            showPayBill={showPayButton(
              claimData?.status, 
              getFeature?.PayYourSentaraBill
            )}
            onPayBill={handlePayBill}
          />
          <hr className="d-md-none d-block" />
          {isAvMed() ? null : (
            <div className="pt-4 print">
              <Paragraph
                text={
                  <Trans
                    i18nKey="medicalClaims.disclaimer"
                    values={{
                      phoneNumber1: phone?.data?.benefitPhone1800,
                      email: t('medicalClaims.disclaimer6'),
                    }}
                    components={{
                      phoneNumber1: (
                        <Link
                          to={`tel:${phone?.data?.benefitPhone1800}`}
                          className="text-decoration-underline"
                        />
                      ),
                      email: (
                        <Link
                          to={`mailto:${t('medicalClaims.disclaimer6')}`}
                          className="text-decoration-underline"
                          data-testid="customerMailId"
                        />
                      ), 
                    }}
                  />
                }
                variant="regular"
                data-testid="combinedDisclaimer"
                className="mb-5 lineBrake"
              />
            </div>
          )}
        </>
      );
    }

    return (
      <Headings
        className="text-center mt-5"
        level={3}
        text={t('medicalClaims.noDataAvailable')}
        variant="regular"
      />
    );
  };

  return (
    <div className="print">
      {(loading || isDownloading) && <Loader />}
      
      {/* Error Modal for EOB download failures */}
      <ErrorModal 
        isOpen={hasError} 
        onClose={clearError}
      />
      
      {renderContent()}
    </div>
  );
};

export default SuperEOBClaims;
